import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTodayCheckins, getCheckinLogs, manualCheckout, autoCheckout, getMemberCheckinHistory } from './api';
import { checkinKeys } from './keys';

export const useTodayCheckins = ({
    centerId,
    enabled = true,
}: {
    centerId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: checkinKeys.today(centerId),
        queryFn: () => getTodayCheckins({ centerId }),
        enabled: enabled && !!centerId,
        select: (data) => {
            if (!data?.data) return data?.data;
            const raw = data.data as any;
            // API 응답: { count, logs[] } → 프론트: { totalCheckins, currentlyIn, checkins[] }
            return {
                totalCheckins: raw.totalCheckins ?? raw.count ?? 0,
                currentlyIn: raw.currentlyIn ?? (raw.logs ?? raw.checkins ?? []).filter((l: any) => !l.checkoutAt).length,
                checkins: raw.checkins ?? raw.logs ?? [],
            };
        },
        refetchInterval: 30000,
    });

export const useCheckinLogs = ({
    centerId,
    startDate,
    endDate,
    search,
    page,
    limit,
    enabled = true,
}: {
    centerId: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...checkinKeys.logs(centerId), startDate, endDate, search, page, limit],
        queryFn: () => getCheckinLogs({ centerId, startDate, endDate, search, page, limit }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });

export const useMemberCheckinHistory = ({
    centerId,
    memberId,
    page,
    limit,
    enabled = true,
}: {
    centerId: string;
    memberId: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...checkinKeys.logs(centerId), 'member', memberId, page, limit],
        queryFn: () => getMemberCheckinHistory({ centerId, memberId, page, limit }),
        enabled: enabled && !!centerId && !!memberId,
        select: (data) => data?.data,
    });

export const useManualCheckout = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: manualCheckout,
        onSuccess: (_, variables) => {
            qc.invalidateQueries({ queryKey: checkinKeys.today(variables.centerId) });
            qc.invalidateQueries({ queryKey: checkinKeys.logs(variables.centerId) });
        },
    });
};

export const useAutoCheckout = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: autoCheckout,
        onSuccess: (_, variables) => {
            qc.invalidateQueries({ queryKey: checkinKeys.today(variables.centerId) });
            qc.invalidateQueries({ queryKey: checkinKeys.logs(variables.centerId) });
        },
    });
};
